import { Controller } from "@hotwired/stimulus"
import { extractPeaks } from "../lib/audio_peaks"
import { drawWaveform } from "../lib/waveform"
import { WaveformLoadState } from "../lib/waveform_load_state.mjs"

// Picking the 30 seconds that sell a band (#122): the track is drawn as a waveform and the clip
// is a window dragged over it, so the chorus is something you aim at instead of a number of
// seconds you guess at.
//
// Enhancement only. The server renders a plain <audio controls> and a number field, both of which
// keep working on their own - this hides them once the waveform is up and leaves them alone if
// decoding fails (a codec the browser won't touch, no Web Audio at all). The number field stays
// the one thing that gets submitted; the window only writes to it.
export default class extends Controller {
  static targets = [
    "file", "audio", "marker", "panel", "stage", "canvas", "window", "playhead",
    "readout", "loading", "failed", "playLabel", "pauseLabel"
  ]
  static values = { clipSeconds: { type: Number, default: 30 }, src: String }

  connect() {
    this.peaks = null
    this.duration = 0
    this.loadState = new WaveformLoadState({
      onLoading: () => {
        this.#surface("begin")
        this.#announce(this.loadingTarget)
      },
      onReady: () => {
        this.#announce(null)
        this.#surface("ready")
      },
      onFailure: () => {
        this.panelTarget.classList.add("hidden")
        this.audioTarget.controls = true
        this.#announce(this.failedTarget)
        this.#surface("failed")
      }
    })
    this.start = this.#markerValue()

    this.redraw = () => this.#render()
    this.resizeObserver = new ResizeObserver(this.redraw)
    this.resizeObserver.observe(this.stageTarget)
    // Canvas pixels don't follow tokens.css on their own - repaint when the scheme flips.
    this.colorScheme = window.matchMedia("(prefers-color-scheme: dark)")
    this.colorScheme.addEventListener("change", this.redraw)

    if (this.srcValue) this.#loadFromUrl(this.srcValue)
  }

  disconnect() {
    this.loadState.cancel()
    this.requestController?.abort()
    this.resizeObserver?.disconnect()
    this.colorScheme?.removeEventListener("change", this.redraw)
    this.#stopFollowing()
    this.#releaseObjectUrl()
  }

  // A file picked in the browser is drawn and played straight from disk - no upload, no
  // round trip, and the band hears the clip before deciding to save it.
  fileChosen() {
    const file = this.fileTarget.files[0]
    if (!file) return

    // A marker from the previous song means nothing in this one.
    this.#moveTo(0)
    this.#load(async () => file)
  }

  timedOut() {
    this.requestController?.abort()
    if (this.loadState.fail(this.loadState.generation)) {
      console.warn("[audio-trimmer] waveform loading timed out")
    }
  }

  // The card already has an upload: fetch it once and use those bytes for both jobs, rather
  // than letting the <audio> element download the same MP3 a second time.
  #loadFromUrl(url) {
    this.#load(async (signal) => {
      const response = await fetch(url, { signal })
      if (!response.ok) throw new Error(`audio request failed with ${response.status}`)

      return response.blob()
    })
  }

  // Every load takes a ticket, and only the newest one may touch the state. Two are easily in
  // flight at once - the stored file downloading while the band picks a replacement - and without
  // this the slower one wins whenever it finishes last, leaving the window aimed at a waveform
  // that belongs to a different song than the one being submitted.
  async #load(source) {
    const load = this.loadState.start()
    this.requestController?.abort()
    const requestController = new AbortController()
    this.requestController = requestController

    try {
      const blob = await source(requestController.signal)
      if (!this.loadState.fetched(load)) return

      this.#playFrom(blob)
      const { peaks, duration } = await extractPeaks(await blob.arrayBuffer())
      if (!this.loadState.decoded(load)) return

      this.peaks = peaks
      this.duration = duration
      this.#ready(load)
    } catch (error) {
      if (!this.loadState.fail(load)) return

      // No waveform to aim at, so the clip starts where the marker already is (zero for a fresh
      // upload) and the band at least gets the plain player back to hear the track.
      console.warn("[audio-trimmer] could not draw the waveform", error)
    } finally {
      if (load === this.loadState.generation) this.requestController = null
    }
  }

  #ready(load) {
    this.panelTarget.classList.remove("hidden")
    // Our own play button drives the element from here on; the native controls would be a second
    // set of transport buttons saying something different.
    this.audioTarget.controls = false
    this.#moveTo(Math.min(this.start, this.#maxStart()))
    this.loadState.drawn(load)
  }

  // Interaction --------------------------------------------------------------------------

  // A click anywhere on the waveform starts the clip there; dragging the window itself is
  // handled below and must not be read as a click on the track underneath it.
  seek(event) {
    if (this.windowTarget.contains(event.target)) return

    this.#moveTo(this.#secondsAt(event.clientX))
  }

  startDrag(event) {
    event.preventDefault()
    this.dragOffset = event.clientX - this.windowTarget.getBoundingClientRect().left
    this.#capture(event)
    this.windowTarget.focus()
    this.dragging = true
  }

  drag(event) {
    if (!this.dragging) return

    this.#moveTo(this.#secondsAt(event.clientX - this.dragOffset))
  }

  endDrag(event) {
    if (!this.dragging) return

    this.dragging = false
    this.#release(event)
  }

  // Capture keeps the drag alive when the pointer runs off the window, which it does the moment
  // you move faster than the box. A synthetic event can carry a pointer id the browser doesn't
  // know - the drag still works, it just isn't captured.
  #capture(event) {
    this.uncaptured = false
    try {
      this.windowTarget.setPointerCapture(event.pointerId)
    } catch {
      this.uncaptured = true
    }
  }

  #release(event) {
    if (this.uncaptured) return

    try {
      this.windowTarget.releasePointerCapture(event.pointerId)
    } catch {
      // The pointer was already released - there is nothing to do about it here.
    }
  }

  // The window is a slider, so it answers the keys a slider answers - this is the whole of
  // keyboard access to the clip.
  key(event) {
    const step = event.shiftKey ? 5 : 1
    const moves = {
      ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step,
      PageDown: -10, PageUp: 10, Home: -this.duration, End: this.duration
    }

    if (event.key === " " || event.key === "Enter") {
      event.preventDefault()
      return this.togglePlay()
    }
    if (!(event.key in moves)) return

    event.preventDefault()
    this.#moveTo(this.start + moves[event.key])
  }

  togglePlay() {
    if (this.audioTarget.paused) this.#playClip()
    else this.audioTarget.pause()
  }

  playing() {
    this.playLabelTarget.classList.add("hidden")
    this.pauseLabelTarget.classList.remove("hidden")
    this.#follow()
  }

  paused() {
    this.playLabelTarget.classList.remove("hidden")
    this.pauseLabelTarget.classList.add("hidden")
    this.#stopFollowing()
    this.playheadTarget.classList.add("hidden")
  }

  // Internals ----------------------------------------------------------------------------

  // play() hands back a promise that rejects when the element has nothing it can play (a refused
  // codec, a source that 404'd). Unhandled, that surfaces as an uncaught error on top of the page.
  #playClip() {
    this.audioTarget.currentTime = this.start
    this.audioTarget.play().catch((error) => {
      console.warn("[audio-trimmer] the browser would not play this file", error)
      this.paused()
    })
  }

  // The playhead runs on rAF rather than `timeupdate`: the element only fires that about four
  // times a second, which reads as a stuttering line over a thirty-second clip.
  #follow() {
    this.playheadTarget.classList.remove("hidden")
    const tick = () => {
      const at = this.audioTarget.currentTime
      if (at >= this.start + this.#clipLength()) return this.audioTarget.pause()

      this.playheadTarget.style.left = `${this.#percent(at)}%`
      this.frame = requestAnimationFrame(tick)
    }
    this.frame = requestAnimationFrame(tick)
  }

  #stopFollowing() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
  }

  #moveTo(seconds) {
    if (!this.audioTarget.paused) this.audioTarget.pause()

    this.start = Math.min(Math.max(seconds, 0), this.#maxStart())
    this.markerTarget.value = this.start.toFixed(2)
    this.#render()
  }

  #render() {
    const end = this.start + this.#clipLength()

    this.windowTarget.style.left = `${this.#percent(this.start)}%`
    this.windowTarget.style.width = `${this.#percent(this.#clipLength())}%`
    this.windowTarget.setAttribute("aria-valuemin", "0")
    this.windowTarget.setAttribute("aria-valuemax", this.#maxStart().toFixed(2))
    this.windowTarget.setAttribute("aria-valuenow", this.start.toFixed(2))
    this.windowTarget.setAttribute("aria-valuetext", `${time(this.start)}–${time(end)}`)
    this.readoutTarget.textContent = `${time(this.start)} – ${time(end)}`

    drawWaveform(this.canvasTarget, this.peaks, { start: this.start, end, duration: this.duration })
  }

  // A song shorter than the clip is all clip: the window covers everything and can't move.
  #clipLength() {
    return Math.min(this.clipSecondsValue, this.duration || this.clipSecondsValue)
  }

  #maxStart() {
    return Math.max(0, this.duration - this.clipSecondsValue)
  }

  #percent(seconds) {
    if (!this.duration) return 0

    return (seconds / this.duration) * 100
  }

  #secondsAt(clientX) {
    const stage = this.stageTarget.getBoundingClientRect()
    if (stage.width === 0) return 0

    return ((clientX - stage.left) / stage.width) * this.duration
  }

  #markerValue() {
    const value = parseFloat(this.markerTarget.value)
    return Number.isFinite(value) && value > 0 ? value : 0
  }

  #playFrom(blob) {
    this.#releaseObjectUrl()
    this.objectUrl = URL.createObjectURL(blob)
    this.audioTarget.src = this.objectUrl
  }

  #releaseObjectUrl() {
    if (!this.objectUrl) return

    URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = undefined
  }

  #surface(state) {
    this.panelTarget.dispatchEvent(new CustomEvent(`loadable:${state}`, { bubbles: true }))
  }

  // One note at a time, or none - the copy itself is server-rendered (i18n).
  #announce(target) {
    for (const note of [this.loadingTarget, this.failedTarget]) {
      note.classList.toggle("hidden", note !== target)
    }
  }
}

function time(seconds) {
  if (!Number.isFinite(seconds)) return "--:--"

  const total = Math.max(0, Math.round(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}
